import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { ConversationEntityType } from '~/components/AIInsightsModal/AIInsightsModal.utils';
import { visitIQAiInsightsSidebarWidth } from '~/constants/localStorage';
import { ConversationSection } from '~/types/api.graphql';

const DEFAULT_SIDEBAR_WIDTH = 400;
const MIN_SIDEBAR_WIDTH = 280;

const computeMaxSidebarWidth = (appDrawerOpen: boolean) => Math.floor(window.innerWidth * (appDrawerOpen ? 0.4 : 0.5));

const readStoredWidth = (maxWidth: number): number => {
  const stored = localStorage.getItem(visitIQAiInsightsSidebarWidth);
  if (!stored) return Math.min(DEFAULT_SIDEBAR_WIDTH, maxWidth);
  const parsed = parseInt(stored, 10);
  return isNaN(parsed)
    ? Math.min(DEFAULT_SIDEBAR_WIDTH, maxWidth)
    : Math.max(MIN_SIDEBAR_WIDTH, Math.min(maxWidth, parsed));
};

export { DEFAULT_SIDEBAR_WIDTH, MIN_SIDEBAR_WIDTH };

interface SidebarOpenParams {
  section?: ConversationSection;
  entityId?: string;
  entityType?: ConversationEntityType;
  entityDeepLink?: string;
}

export interface ExpandedSlotRect {
  left: number;
  width: number;
}

interface AIInsightsSidebarContextValue {
  isOpen: boolean;
  isExpanded: boolean;
  isExpandFading: boolean;
  expandedOriginPath: string | null;
  expandedSlotRect: ExpandedSlotRect | null;
  section: ConversationSection | null;
  entityId: string | null;
  entityType: ConversationEntityType | null;
  entityDeepLink: string | null;
  sidebarWidth: number;
  maxSidebarWidth: number;
  isResizing: boolean;
  conversationId: string | null;
  clickCounterCTA: number;
  setSidebarWidth: (width: number) => void;
  setIsResizing: (resizing: boolean) => void;
  setConversationId: (id: string | null) => void;
  setIsAppDrawerOpen: (open: boolean) => void;
  setIsExpandFading: (v: boolean) => void;
  openSidebar: (params: SidebarOpenParams) => void;
  closeSidebar: () => void;
  updateEntityDeepLink: (deepLink: string) => void;
  skipExpandAnimation: boolean;
  expand: (originPath: string, options?: { immediate?: boolean }) => void;
  collapse: () => void;
  setExpandedSlotRect: (rect: ExpandedSlotRect | null) => void;
  incrementChatEntryCount: () => void;
  decrementChatEntryCount: () => void;
  resetChatEntryCount: () => void;
}

const AIInsightsSidebarContext = createContext<AIInsightsSidebarContextValue | null>(null);

export const AIInsightsSidebarProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExpandFading, setIsExpandFading] = useState(false);
  const [skipExpandAnimation, setSkipExpandAnimation] = useState(false);
  const [expandedOriginPath, setExpandedOriginPath] = useState<string | null>(null);
  const [expandedSlotRect, setExpandedSlotRect] = useState<ExpandedSlotRect | null>(null);
  const [section, setSection] = useState<ConversationSection | null>(null);
  const [entityId, setEntityId] = useState<string | null>(null);
  const [entityType, setEntityType] = useState<ConversationEntityType | null>(null);
  const [entityDeepLink, setEntityDeepLink] = useState<string | null>(null);
  const [isAppDrawerOpen, setIsAppDrawerOpen] = useState(false);
  const [maxSidebarWidth, setMaxSidebarWidth] = useState(() => computeMaxSidebarWidth(false));
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => readStoredWidth(computeMaxSidebarWidth(false)));
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    const update = () => {
      const newMax = computeMaxSidebarWidth(isAppDrawerOpen);
      setMaxSidebarWidth(newMax);
      setSidebarWidth((prev) => Math.min(prev, newMax));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [isAppDrawerOpen]);

  useEffect(() => {
    document.body.classList.toggle('ai-sidebar-open', isOpen);
    return () => document.body.classList.remove('ai-sidebar-open');
  }, [isOpen]);

  // Tracks browser-history entries pushed while inside the expanded chat.
  // Strategy: push only on empty→detail transitions; replace on all other
  // in-chat nav. On collapse, navigate(-count) pops all chat entries so
  // pressing Back from the origin page never re-opens the chat.
  const chatEntryCountRef = useRef(0);

  const incrementChatEntryCount = useCallback(() => {
    chatEntryCountRef.current++;
  }, []);

  const decrementChatEntryCount = useCallback(() => {
    chatEntryCountRef.current = Math.max(0, chatEntryCountRef.current - 1);
  }, []);

  const resetChatEntryCount = useCallback(() => {
    chatEntryCountRef.current = 0;
  }, []);

  const [conversationId, setConversationId] = useState<string | null>(null);
  // to know that a CTA for same section is clicked
  const [clickCounterCTA, setClickCounterCTA] = useState(0);

  const openSidebar = useCallback(
    ({ section = null, entityId = null, entityType = null, entityDeepLink }: SidebarOpenParams) => {
      setSection(section);
      setEntityId(entityId);
      setEntityType(entityType);
      setEntityDeepLink(entityDeepLink ?? null);
      if (!isOpen) {
        setConversationId(null);
      }
      setIsOpen(true);
      setClickCounterCTA((value) => value + 1);
    },
    [isOpen],
  );

  const closeSidebar = useCallback(() => {
    setConversationId(null);
    setIsOpen(false);
  }, []);

  const expand = useCallback(
    (originPath: string, options?: { immediate?: boolean }) => {
      resetChatEntryCount();
      // 1 accounts for the push done by handleExpand in AIInsightsSidebar
      chatEntryCountRef.current = 1;
      setIsExpanded(true);
      setExpandedOriginPath(originPath);
      setIsOpen(true);
      setIsExpandFading(false);
      if (options?.immediate) {
        setSkipExpandAnimation(true);
      }
    },
    [resetChatEntryCount],
  );

  const collapse = useCallback(() => {
    resetChatEntryCount();
    setIsExpanded(false);
    setExpandedOriginPath(null);
    setExpandedSlotRect(null);
    setSkipExpandAnimation(false);
  }, [resetChatEntryCount]);

  // Auto-clear skipExpandAnimation once the layout has settled
  useEffect(() => {
    if (!skipExpandAnimation || !isExpanded || !expandedSlotRect) return undefined;
    const id = requestAnimationFrame(() => setSkipExpandAnimation(false));
    return () => cancelAnimationFrame(id);
  }, [skipExpandAnimation, isExpanded, expandedSlotRect]);

  const updateEntityDeepLink = useCallback((deepLink: string) => {
    setEntityDeepLink(deepLink);
  }, []);

  return (
    <AIInsightsSidebarContext.Provider
      value={{
        isOpen,
        isExpanded,
        isExpandFading,
        skipExpandAnimation,
        expandedOriginPath,
        expandedSlotRect,
        section,
        entityId,
        entityType,
        entityDeepLink,
        sidebarWidth,
        maxSidebarWidth,
        isResizing,
        conversationId,
        clickCounterCTA,
        setSidebarWidth,
        setIsResizing,
        setConversationId,
        setIsAppDrawerOpen,
        setIsExpandFading,
        openSidebar,
        closeSidebar,
        updateEntityDeepLink,
        expand,
        collapse,
        setExpandedSlotRect,
        incrementChatEntryCount,
        decrementChatEntryCount,
        resetChatEntryCount,
      }}
    >
      {children}
    </AIInsightsSidebarContext.Provider>
  );
};

export const useAIInsightsSidebar = () => {
  const ctx = useContext(AIInsightsSidebarContext);
  if (!ctx) throw new Error('useAIInsightsSidebar must be used within AIInsightsSidebarProvider');
  return ctx;
};
